import { Component, ComponentChild } from 'preact'

import formatDateInLocalTimezone from '@insided/shared/utils/dateFormatting'
import { ContentType } from '@insided/shared/utils/contentType'
import {
  getIdeaStatuses,
  getModerationLabels,
  getPoll,
  getReplies,
  getReply,
  markAsReadByTeam
} from './services/topicService'
import { markPostsAsSpam, markPostsAsNotSpam } from './services/spamService'

/* eslint-disable import/no-internal-modules */
import { ProductAreaType } from '@insided/deprecated-control/product-areas/ProductAreaSelect'
import productAreaService from '@insided/deprecated-services/productAreaService'
import WarningBox from '@insided/deprecated-control/ui-components/WarningBox'
import TeamNotes from './TeamNotes'
import AlertComponent from '@insided/deprecated-control/ui-components/AlertComponent'
import { User } from '@insided/deprecated-preact-components/user-selector/User'
import { getInsidedData } from '@insided/destination/utils/insidedDataStorage'
import ContentHelpfulnessPerformance from '@insided/deprecated-control/content-helpfulness/contentHelpfulnessPerformance'
import { TranslationCategory } from '@insided/deprecated-libs/translationsUtils/translations'
import { fetchPhrase } from '@insided/deprecated-libs/translationsUtils/phrase'

import {
  Reply,
  ModerationLabel,
  IdeaStatus,
  Author,
  TopicModerationProps,
  TopicModerationState,
  Poll
} from './interfaces'
import FeaturedImagePanel from './FeaturedImagePanel'
import MarkAsSpamModal from './MarkAsSpam/MarkAsSpamModal'
import MarkAsNotSpamModal from './MarkAsSpam/MarkAsNotSpamModal'
import TopicInfo from './TopicInfo/TopicInfo'
import TopicHeader from './TopicHeader/TopicHeader'
import OpeningPost from './post/OpeningPost'
import TopicDetails from './TopicDetails'
import Replies from './post/Replies'
import {
  ConfigContext,
  filterSelectedReplies,
  getAllVisibleReplies,
  getCategoryMetadata,
  getPostOffset,
  normalizeAuthor,
  normalizeCategoriesAndGroups,
  getRepliesPaginationParamsFromURL,
  getFormattedVotesData
} from './utils'
import TopicFooter from './TopicFooter'
import AddReply from './AddReply'

class TopicModeration extends Component<TopicModerationProps, TopicModerationState> {
  private readonly baseApiUrl: string
  private readonly userId: string = getInsidedData('user').userid
  private onScrollTimerId: number | undefined

  constructor(props: TopicModerationProps) {
    super(props)
    const repliesPagination = getRepliesPaginationParamsFromURL()

    this.state = {
      ...props.topic,
      author: normalizeAuthor(props.topic.author),
      votes: props.topic.votedBy?.length,
      likes: props.topic.likedBy?.length,
      views: props.topic.views,
      categoryId: props.topic.categoryId,
      poll: props.topic.poll ?? null,
      selectedProductAreas: props.topic.productAreas || [],
      showEditor: false,
      isLoading: null,
      isMarkAsSpamModalOpen: false,
      isMarkAsNotSpamModalOpen: false,
      allProductAreas: [],
      selectedModerationLabel: props.topic.moderationLabel || null,
      ideaStatus: props.topic.ideaStatus || null,
      moderationLabels: [],
      ideaStatuses: [],
      alert: null,
      pinnedReply: null,
      repliesPagination: repliesPagination,
      repliesSorting: 'oldestFirst',
      selectedPublicLabel: props.topic.publicLabel,
      publicLabelText: '',
      selectedReplies: [],
      visibleReplyCount: null,
      bestAnswer: null,
      reported: props.topic.reported || false,
      reportedContent: props.topic.reportedContent || []
    }

    this.baseApiUrl = `/community/api/${props.contentType}s`

    this.onTitleChanged = this.onTitleChanged.bind(this)
    this.onPublicTagsChanged = this.onPublicTagsChanged.bind(this)
    this.onModeratorTagsChanged = this.onModeratorTagsChanged.bind(this)
    this.onClosedChanged = this.onClosedChanged.bind(this)
    this.onProductAreasChanged = this.onProductAreasChanged.bind(this)
    this.handleChangeVote = this.handleChangeVote.bind(this)
    this.handleChangeLike = this.handleChangeLike.bind(this)
    this.onTrash = this.onTrash.bind(this)
    this.handleAddReply = this.handleAddReply.bind(this)
    this.onIdeationStatusChanged = this.onIdeationStatusChanged.bind(this)
    this.onPublicLabelInput = this.onPublicLabelInput.bind(this)
    this.onPublicLabelChanged = this.onPublicLabelChanged.bind(this)
    this.onIdeationStatusChanged = this.onIdeationStatusChanged.bind(this)
    this.handleModeratorAssigned = this.handleModeratorAssigned.bind(this)
    this.handleShowAlert = this.handleShowAlert.bind(this)
    this.handleToggleSpamModal = this.handleToggleSpamModal.bind(this)
    this.getReplies = this.getReplies.bind(this)
    this.getPoll = this.getPoll.bind(this)
    this.setMessage = this.setMessage.bind(this)
    this.onChangeAuthor = this.onChangeAuthor.bind(this)
    this.onApprove = this.onApprove.bind(this)
    this.onResolve = this.onResolve.bind(this)
    this.handleChangeContent = this.handleChangeContent.bind(this)
    this.onShowEditor = this.onShowEditor.bind(this)
    this.onCancelEditor = this.onCancelEditor.bind(this)
    this.setSelectedReplies = this.setSelectedReplies.bind(this)
    this.setReplies = this.setReplies.bind(this)
    this.setRepliesSorting = this.setRepliesSorting.bind(this)
    this.onTogglePin = this.onTogglePin.bind(this)
    this.onStickyChanged = this.onStickyChanged.bind(this)
    this.onBestAnswerChanged = this.onBestAnswerChanged.bind(this)
    this.onReplyTrashed = this.onReplyTrashed.bind(this)
    this.onCategoryChanged = this.onCategoryChanged.bind(this)
    this.onPollUpdate = this.onPollUpdate.bind(this)
  }

  async componentDidMount() {
    const {
      config: { contentTypeConfig },
      contentType,
      topic
    } = this.props

    await this.getReplies()

    if (contentTypeConfig.poll && this.state.poll) {
      await this.getPoll()
    }

    if (contentTypeConfig.productAreas) {
      const productAreas = await productAreaService.productAreasList()
      this.setState({ allProductAreas: productAreas })
    }

    if (contentTypeConfig.moderationLabel) {
      const moderationLabels = await getModerationLabels()
      this.setState({ moderationLabels })
    }

    if (contentTypeConfig.ideaStatus) {
      const ideaStatuses = await getIdeaStatuses()
      this.setState({ ideaStatuses })
    }

    if (topic.pinnedReplyId) {
      const pinnedReply = await getReply(contentType, topic.id, Number(topic.pinnedReplyId))
      this.setState({ pinnedReply })
    }

    if (topic.answer) {
      const bestAnswer = await getReply(contentType, topic.id, Number(topic.answer))
      this.setState({ bestAnswer })
    }

    const locationHash = window.location.hash
    const anchorPostId = locationHash.slice(locationHash.indexOf('/') + 1)
    if (anchorPostId) {
      this.onScrollTimerId = window.setTimeout(() => {
        const postOffset = getPostOffset(anchorPostId)
        window.scrollTo({ behavior: 'smooth', top: postOffset })
      }, 500)
    }

    await this.preFetchPhrases()

    await markAsReadByTeam(contentType, topic.id)
  }

  componentWillUnmount() {
    if (this.onScrollTimerId) {
      window.clearTimeout(this.onScrollTimerId)
    }
  }

  async preFetchPhrases() {
    await new Promise((resolve) => fetchPhrase('Control' as TranslationCategory, 'topic_moderation.reported_content.resolve.private_message', (phrase) => resolve(phrase)))
  }

  onTitleChanged(title: string) {
    this.setState({ title })
  }

  onProductAreasChanged(productAreas: ProductAreaType[]) {
    this.setState({ productAreas })
  }

  onPublicTagsChanged(publicTags: string[]) {
    this.setState({ tags: publicTags })
  }

  onModeratorTagsChanged(moderatorTags: string[]) {
    this.setState({ moderatorTags })
  }

  onClosedChanged(closed: boolean) {
    this.setState({ closed })
  }

  onStickyChanged(sticky: boolean) {
    this.setState({ sticky })
  }

  handleChangeContent(content: string) {
    this.setState({ content })
  }

  handleChangeVote(votes: number, votedBy: string[]) {
    this.setState({ votes, votedBy })
  }

  handleChangeLike(likes: number, likedBy: string[]) {
    this.setState({ likes, likedBy })
  }

  onTrash(trashed: boolean) {
    this.setState({ trashed })
    if (trashed) {
      this.setState({ pendingApproval: false })
    }
  }

  async handleAddReply() {
    await this.getReplies()
  }

  async getPoll() {
    const { contentType, topic } = this.props
    try {
      const pollResponse = await getPoll(contentType, Number(topic.id))
      if (pollResponse) {
        const { options, voteCount } = getFormattedVotesData(pollResponse.votes)
        this.setState({
          poll: {
            ...this.state.poll,
            question: pollResponse.title,
            votes: pollResponse.votes,
            options,
            voteCount
          }
        })
      }
    } catch (error) {
      this.setState({
        poll: this.state.poll
      })
    }
  }

  async getReplies(
    page: number | undefined = this.state.repliesPagination.page,
    pageSize: number | undefined = this.state.repliesPagination.pageSize,
    sort: string = this.state.repliesSorting
  ) {
    const { contentType, topic } = this.props
    const repliesResponse = await getReplies(contentType, Number(topic.id), page, pageSize, sort)

    if (repliesResponse?.result) {
      const serverReplies = repliesResponse.result
      const { offset, limit, totalCount } = repliesResponse._metadata
      const replies = serverReplies.map((reply: Reply) => {
        return {
          ...reply,
          author: normalizeAuthor(reply.author),
          reported: reply.reported || false,
          reportedContent: reply.reportedContent || []
        }
      })
      this.setState({
        replies,
        repliesPagination: {
          page: offset / limit + 1,
          pageSize: limit,
          total: totalCount
        },
        visibleReplyCount: getAllVisibleReplies(replies ?? []).length
      })
    }
  }

  handleModeratorAssigned(user?: User) {
    this.setState({ assignedModerator: user })
  }

  onModerationLabelChanged = async (moderationLabel: ModerationLabel | null) => {
    this.setState({ selectedModerationLabel: moderationLabel })
  }

  onIdeationStatusChanged(ideationStatus: IdeaStatus) {
    this.setState({ ideaStatus: ideationStatus })
  }

  onPublicLabelInput(publicLabel: string) {
    this.setState({ publicLabelText: publicLabel })
  }

  onPublicLabelChanged(newPublicLabel: string | null) {
    this.setState({
      selectedPublicLabel: newPublicLabel,
      publicLabelText: ''
    })
  }

  onCategoryChanged(categoryId: number) {
    this.setState({ categoryId })
  }

  handleToggleMarkAsSpamModal = () => {
    const { isMarkAsSpamModalOpen } = this.state
    if (isMarkAsSpamModalOpen) {
      this.setState({ selectedReplyId: undefined })
    }
    this.setState({ isMarkAsSpamModalOpen: !isMarkAsSpamModalOpen })
  }

  handleToggleMarkAsNotSpamModal = () => {
    const { isMarkAsNotSpamModalOpen } = this.state
    if (isMarkAsNotSpamModalOpen) {
      this.setState({ selectedReplyId: undefined })
    }
    this.setState({ isMarkAsNotSpamModalOpen: !isMarkAsNotSpamModalOpen })
  }

  getSelectedReply = (): Reply => {
    const { replies, selectedReplyId } = this.state
    return replies?.find((reply) => reply.id === selectedReplyId) as Reply
  }

  handleToggleSpamModal(replyId?: string) {
    let isPostSpam = this.state.spam
    if (typeof replyId === 'string') {
      this.setState({ selectedReplyId: replyId })
      const selectedReply = this.getSelectedReply()
      isPostSpam = selectedReply?.spam
    }
    isPostSpam ? this.handleToggleMarkAsNotSpamModal() : this.handleToggleMarkAsSpamModal()
  }

  handleToggleSpam = async () => {
    const { id, spam, selectedReplyId } = this.state
    const { contentType } = this.props

    let isPostSpam = spam
    const payload = {
      id: Number(id),
      contentType: contentType
    }

    try {
      if (selectedReplyId) {
        const selectedReply = this.getSelectedReply()
        isPostSpam = selectedReply.spam
        payload.id = Number(selectedReplyId)
        payload.contentType = `${contentType}_reply` as ContentType
      }

      if (isPostSpam) {
        await markPostsAsNotSpam([payload])
        this.handleToggleMarkAsNotSpamModal()
      } else {
        await markPostsAsSpam([payload])
        this.handleToggleMarkAsSpamModal()
      }

      if (selectedReplyId) {
        await this.getReplies()
      } else {
        this.setState({ spam: !spam }, () => {
          if (this.state.spam) {
            this.setState({ pendingApproval: false })
          }
        })
      }

      const entity = selectedReplyId === undefined ? 'topic' : 'post'
      if (!isPostSpam) {
        this.setState((previousState) => {
          const { selectedReplies } = previousState
          return {
            selectedReplies: filterSelectedReplies(selectedReplies, selectedReplyId)
          }
        })
      }
      this.handleShowAlert(
        'success',
        isPostSpam ? `You’ve marked the ${entity} as not spam.` : `You’ve marked the ${entity} as spam.`
      )
    } catch (e) {
      this.handleShowAlert('error')
    }
  }

  handleShowAlert(type: 'success' | 'error', message?: string) {
    this.setState({
      alert: {
        type: type,
        message: message ?? 'Something went wrong, please try again.'
      }
    })
  }

  setMessage(message?: ComponentChild) {
    this.setState({ message })
  }

  onChangeAuthor(author: Author) {
    this.setState({ author })
  }

  onPollUpdate(newValue: Pick<Poll, 'question'> | Pick<Poll, 'options'>) {
    if (this.state.poll) {
      const { poll } = this.state
      const newPoll = { ...poll, ...newValue }
      this.setState({ poll: newPoll })
    }
  }

  onApprove() {
    this.setState({ pendingApproval: false })
  }

  onResolve() {
    this.setState({ reported: false, reportedContent: [] })
  }

  onShowEditor() {
    this.setState({ showEditor: true })
  }

  onCancelEditor() {
    this.setState({ showEditor: false })
  }

  onTogglePin(reply: Reply) {
    return (pinnedReplyId: string | null) => {
      this.setState({
        pinnedReplyId,
        pinnedReply: pinnedReplyId ? reply : null
      })
    }
  }

  onBestAnswerChanged(bestAnswer: Reply | null) {
    this.setState({ bestAnswer })
  }

  onReplyTrashed(replyId: string) {
    const { bestAnswer } = this.state
    if (bestAnswer?.id === replyId) {
      this.setState({ bestAnswer: null })
    }
  }

  setSelectedReplies(selectedReplies: string[]) {
    this.setState({ selectedReplies })
  }

  setReplies(replies: Reply[]) {
    this.setState({ replies })
  }

  setRepliesSorting(repliesSorting: string) {
    this.setState({ repliesSorting })
  }

  hasPinnedReply = () => !!this.props.topic.pinnedReplyId

  render() {
    const {
      topic,
      contentType,
      isProductboardNoteCreationEnabled,
      isZendeskTicketEscalationEnabled,
      isSalesforceCaseEscalationEnabled,
      isFreshdeskTicketEscalationEnabled,
      isIntercomCreateConversationEnabled,
      destinationUrl,
      categories,
      phrases,
      config,
      config: { contentTypeConfig, moderationConfig }
    } = this.props
    const {
      title,
      author,
      tags,
      moderatorTags,
      selectedProductAreas,
      closed,
      content,
      featuredImage,
      allProductAreas,
      moderationLabels,
      selectedModerationLabel,
      selectedPublicLabel,
      likes = 0,
      views = 0,
      votes = 0,
      likedBy,
      votedBy,
      trashed,
      showEditor,
      isMarkAsSpamModalOpen,
      isMarkAsNotSpamModalOpen,
      alert,
      replies,
      repliesPagination,
      repliesSorting,
      ideaStatus,
      pinnedReplyId,
      pinnedReply,
      poll,
      spam,
      ideaStatuses,
      publicLabelText,
      assignedModerator,
      message,
      pendingApproval,
      selectedReplyId,
      selectedReplies,
      visibleReplyCount,
      sticky,
      bestAnswer,
      categoryId,
      reported,
      reportedContent
    } = this.state

    const publishedAt = formatDateInLocalTimezone(
      new Date(topic.submittedAt ?? (topic.publishedAt as string)).getTime() / 1000
    )
    const replyCount = repliesPagination.total ?? 0
    const showTopicFooter = Boolean(selectedReplies.length)
    const normalisedCategories = categories && normalizeCategoriesAndGroups(categories)
    const { category, forum } = normalisedCategories && getCategoryMetadata(normalisedCategories, categoryId)

    return (
      <ConfigContext.Provider value={config}>
        <div>
          {alert && (
            <AlertComponent
              sticky
              isVisible
              message={alert.message}
              messageType={alert.type}
              isVisibleFor={3000}
              onTimeoutEnd={() => this.setState({ alert: null })}
              renderHTML
            />
          )}
          <TopicHeader
            topic={topic}
            contentType={contentType}
            ideationStatus={ideaStatus}
            ideationStatuses={ideaStatuses}
            getReplies={this.getReplies}
            onModeratorAssigned={this.handleModeratorAssigned}
            selectedPublicLabel={selectedPublicLabel}
            onIdeationStatusChanged={this.onIdeationStatusChanged}
            onPublicLabelChanged={this.onPublicLabelChanged}
            onPublicLabelInput={this.onPublicLabelInput}
            onShowAlert={this.handleShowAlert}
            handleToggleSpamModal={this.handleToggleSpamModal}
            onTrash={this.onTrash}
            publicLabelText={publicLabelText}
            trashed={trashed}
            reported={reported}
            spam={spam}
            assignedModerator={assignedModerator}
            categories={categories}
            setMessage={this.setMessage}
            hasPinnedReply={!!pinnedReply}
            phrases={phrases}
            onChangeAuthor={this.onChangeAuthor}
          />
          {message && <WarningBox message={message as string} />}
          <TopicInfo
            publishedAt={publishedAt}
            spam={spam}
            contentType={contentType}
            publicId={topic.publicId}
            replyCount={replyCount}
            title={title}
            trashed={trashed}
            views={views}
            publicLabel={selectedPublicLabel}
            ideationStatus={ideaStatus}
            votes={contentTypeConfig.vote ? votes : undefined}
            likes={contentTypeConfig.like ? likes : undefined}
            category={contentTypeConfig.forum ? category : undefined}
            forum={contentTypeConfig.forum ? forum : undefined}
            pending={pendingApproval}
            destinationUrl={destinationUrl}
            sticky={sticky}
            hasBestAnswer={Boolean(bestAnswer?.id)}
            reported={reported}
          />
          <TopicDetails
            contentType={contentType}
            privateId={topic.id}
            publicId={Number(topic.publicId)}
            onTitleChanged={this.onTitleChanged}
            onProductAreasChanged={this.onProductAreasChanged}
            onPublicTagsChanged={this.onPublicTagsChanged}
            onCategoryChanged={this.onCategoryChanged}
            onModeratorTagsChanged={this.onModeratorTagsChanged}
            onClosedChanged={this.onClosedChanged}
            onStickyChanged={this.onStickyChanged}
            onModerationLabelChanged={this.onModerationLabelChanged}
            title={title}
            allProductAreas={allProductAreas}
            productAreas={selectedProductAreas}
            publicTags={tags}
            categories={contentTypeConfig.forum ? categories : undefined}
            categoryId={categoryId}
            moderatorTags={moderatorTags}
            moderationLabels={moderationLabels}
            selectedModerationLabel={selectedModerationLabel}
            closed={closed}
            sticky={sticky}
            onShowAlert={this.handleShowAlert}
          />
          {contentTypeConfig.featuredImage && (
            <FeaturedImagePanel contentType={contentType} featuredImage={featuredImage} privateId={topic.id} />
          )}
          <TeamNotes
            topicContentType={contentType}
            topicId={topic.publicId}
            topicTitle={topic.title}
            topicUrl={window.location.href}
          />
          {moderationConfig.contentHelpfulness && <ContentHelpfulnessPerformance topicId={topic.publicId} />}
          <section id='topic-moderation--posts-container'>
            <h4>
              <strong>Opening post</strong>
            </h4>
            <OpeningPost
              author={author}
              publishedAt={publishedAt}
              spam={spam}
              pending={pendingApproval}
              trashed={trashed}
              content={content}
              onChangeContent={this.handleChangeContent}
              topicId={topic.id}
              contentType={contentType}
              poll={poll}
              onPollUpdate={this.onPollUpdate}
              isProductboardNoteCreationEnabled={isProductboardNoteCreationEnabled}
              isSalesforceCaseEscalationEnabled={isSalesforceCaseEscalationEnabled}
              isZendeskTicketEscalationEnabled={isZendeskTicketEscalationEnabled}
              isFreshdeskTicketEscalationEnabled={isFreshdeskTicketEscalationEnabled}
              isIntercomCreateConversationEnabled={isIntercomCreateConversationEnabled}
              title={title}
              destinationUrl={destinationUrl}
              showEditor={showEditor}
              onShowEditor={this.onShowEditor}
              onCancel={this.onCancelEditor}
              onApprove={this.onApprove}
              onResolve={this.onResolve}
              onShowAlert={this.handleShowAlert}
              votes={votes}
              onChangeVote={this.handleChangeVote}
              likes={likes}
              votedBy={votedBy ?? []}
              likedBy={likedBy ?? []}
              onChangeLike={this.handleChangeLike}
              userId={this.userId}
              pinnedReply={contentTypeConfig.pinReply && replies && pinnedReplyId ? pinnedReply : undefined}
              bestAnswer={bestAnswer}
              publicId={topic.publicId}
              visibleReplyCount={visibleReplyCount}
              reported={reported}
              reportedContent={reportedContent}
            />
            {replies && (
              <Replies
                replyCount={replyCount}
                replies={replies}
                repliesPagination={repliesPagination}
                getReplies={this.getReplies}
                repliesSorting={repliesSorting}
                setRepliesSorting={this.setRepliesSorting}
                author={author}
                bestAnswerId={bestAnswer?.id}
                pinnedReplyId={pinnedReplyId}
                contentType={contentType}
                topic={topic}
                title={title}
                handleShowAlert={this.handleShowAlert}
                handleToggleSpamModal={this.handleToggleSpamModal}
                selectedReplies={selectedReplies}
                setSelectedReplies={this.setSelectedReplies}
                destinationUrl={destinationUrl}
                setReplies={this.setReplies}
                onTogglePin={this.onTogglePin}
                onBestAnswerChanged={this.onBestAnswerChanged}
                isProductboardNoteCreationEnabled={isProductboardNoteCreationEnabled}
                isZendeskTicketEscalationEnabled={isZendeskTicketEscalationEnabled}
                isSalesforceCaseEscalationEnabled={isSalesforceCaseEscalationEnabled}
                isFreshdeskTicketEscalationEnabled={isFreshdeskTicketEscalationEnabled}
                isIntercomCreateConversationEnabled={isIntercomCreateConversationEnabled}
                categories={categories}
                onReplyTrashed={this.onReplyTrashed}
              />
            )}
          </section>
          <AddReply topicId={topic.id} handleAddReply={this.handleAddReply} contentType={contentType} />
          {isMarkAsNotSpamModalOpen && (
            <MarkAsNotSpamModal
              isOpen={isMarkAsNotSpamModalOpen}
              onClose={this.handleToggleMarkAsNotSpamModal}
              onToggleSpam={this.handleToggleSpam}
              selectedReplyId={selectedReplyId}
            />
          )}
          {isMarkAsSpamModalOpen && (
            <MarkAsSpamModal
              isOpen={isMarkAsSpamModalOpen}
              onClose={this.handleToggleMarkAsSpamModal}
              onToggleSpam={this.handleToggleSpam}
              selectedReplyId={selectedReplyId}
            />
          )}
          <TopicFooter
            showTopicFooter={showTopicFooter}
            count={selectedReplies.length}
            replies={replies}
            contentType={contentType}
            setSelectedReplies={this.setSelectedReplies}
            getReplies={this.getReplies}
            handleShowAlert={this.handleShowAlert}
            selectedReplies={selectedReplies}
            onShowAlert={this.handleShowAlert}
          />
        </div>
      </ConfigContext.Provider>
    )
  }
}

export default TopicModeration
